import { Directive, ElementRef, Host, Input, OnDestroy } from '@angular/core';

import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';

import { Breakpoint, BreakpointService } from '@hdms/shared/util-core';

@Directive()
export abstract class PopoverDirectiveAbstract implements OnDestroy {
  static activeInstance: PopoverDirectiveAbstract;

  public isOpen = false;

  @Input() popoverContext: { $implicit: unknown };

  protected readonly unsubscribe$ = new Subject<void>();
  protected popoverBody: HTMLElement;

  protected constructor(
    protected readonly elRef: ElementRef,
    @Host() public readonly ngbPopover: NgbPopover,
    protected readonly breakpointService: BreakpointService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribeAll();
    this.unsubscribe$.complete();
    this.resetActiveInstance();
  }

  public close(): void {
    if (this.isOpen) {
      this.ngbPopover.close();
    }
  }

  protected configureNgbPopover(): void {
    this.ngbPopover.autoClose = 'outside';

    // ngbPopover doesn't provide `open`/`close` events/callbacks/whatever
    // so we hijack the original open/close() methods for invoking dependent internal logic
    const orgOpen = this.ngbPopover.open.bind(this.ngbPopover);
    this.ngbPopover.open = (ctx: unknown) => {
      if (this.popoverContext) {
        ctx = this.popoverContext;
      }
      orgOpen(ctx);
      this.onOpen();
    };
    const orgClose = this.ngbPopover.close.bind(this.ngbPopover);
    this.ngbPopover.close = () => {
      orgClose();
      this.onClose();
    };
  }

  protected getTriggerElm(): HTMLElement {
    return this.elRef.nativeElement;
  }

  protected getStickyParent(): Element {
    return (
      // only in desktop bp up, as on mobile flyouts will always appear as fullscreen overlays
      this.isWithinDesktopBreakpoint() &&
      (this.getTriggerElm().closest('.sticky-top') || this.getTriggerElm().closest('.sticky-top-stacked'))
    );
  }

  protected getPopoverElm(): HTMLElement {
    return (this.ngbPopover as any)._windowRef.location.nativeElement;
  }

  protected getPopoverBodyElm(): HTMLElement {
    return this.getPopoverElm().querySelector('.popover-body');
  }

  protected wrapPopover(wrapper: Element): void {
    wrapper.appendChild(this.getPopoverElm());
  }

  protected unsubscribeAll(): void {
    this.unsubscribe$.next();
  }

  protected onOpen(): void {
    PopoverDirectiveAbstract.activeInstance = this;
    this.popoverBody = this.getPopoverBodyElm();
    this.isOpen = true;
  }
  protected onClose(): void {
    this.isOpen = false;
    this.unsubscribeAll();
  }

  protected isActiveInstance(): boolean {
    return PopoverDirectiveAbstract.activeInstance === this;
  }

  protected resetActiveInstance(): void {
    if (PopoverDirectiveAbstract.activeInstance === this) {
      PopoverDirectiveAbstract.activeInstance = null;
    }
  }

  protected isChildOfPopover(element: Element): boolean {
    return PopoverDirectiveAbstract.activeInstance?.popoverBody.contains(element);
  }

  protected isWithinDesktopBreakpoint(): boolean {
    return this.breakpointService.isWithinMinBound(Breakpoint.MD);
  }
}
